import { useStore } from '@nanostores/react';
import { useState } from 'react';
import { chatId } from '~/lib/persistence/useChatHistory';
import { Segmented } from '~/components/workbench/tools/payments/ui';
import { AutomationBuilder } from './AutomationBuilder';
import { AutomationsList } from './AutomationsList';
import { RunLog } from './RunLog';

type View = { kind: 'list' } | { kind: 'builder'; id: string | null } | { kind: 'runs'; automationId?: string };

/**
 * Owner-facing Automations panel: "when this happens, do that". The list with
 * on/off switches and templates, a builder (trigger → conditions → steps) and
 * the run log. The app id IS the chat id.
 */
export function AutomationsPanel() {
  const appId = useStore(chatId);

  return (
    <div className="relative flex h-full flex-col">
      <div className="border-b border-hairline px-4 py-3.5">
        <div className="flex items-center gap-2.5">
          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-accent/10 ring-1 ring-inset ring-accent/15">
            <div className="i-ph:lightning text-[13px] text-accent" />
          </div>
          <h3 className="text-[13px] font-medium tracking-tight text-primary">Automations</h3>
        </div>
        <p className="mt-2 text-[11px] leading-relaxed text-muted">
          When something happens on your site, do something: email, webhooks, discount codes, notes.
        </p>
      </div>

      {appId ? (
        <AutomationsPanelBody key={appId} appId={appId} />
      ) : (
        <div className="flex flex-1 items-center justify-center px-6 text-center text-[12px] text-muted">
          Start building to add automations.
        </div>
      )}
    </div>
  );
}

function AutomationsPanelBody({ appId }: { appId: string }) {
  const [view, setView] = useState<View>({ kind: 'list' });
  const [listVersion, setListVersion] = useState(0);
  const back = () => {
    setView({ kind: 'list' });
    setListVersion((v) => v + 1);
  };

  return (
    <div className="flex-1 space-y-5 overflow-y-auto p-4">
      {view.kind !== 'builder' && (
        <Segmented
          label="Automations view"
          value={view.kind}
          options={[
            ['list', 'Automations'],
            ['runs', 'Run log'],
          ]}
          onChange={(k) => (k === 'list' ? back() : setView({ kind: 'runs' }))}
        />
      )}

      {view.kind === 'list' && (
        <AutomationsList
          key={listVersion}
          appId={appId}
          onOpen={(id) => setView({ kind: 'builder', id })}
          onRuns={(automationId) => setView({ kind: 'runs', automationId })}
        />
      )}
      {view.kind === 'builder' && <AutomationBuilder appId={appId} automationId={view.id} onBack={back} />}
      {view.kind === 'runs' && (
        <RunLog appId={appId} automationId={view.automationId} onClearFilter={() => setView({ kind: 'runs' })} />
      )}
    </div>
  );
}
